import React, { useContext, useEffect, useState } from "react";
import DashboardContainer from "../HomeContainer/Container";
import { TimeBookingContext } from "../../context/TimebookingContext";
import { ITsWoche, ITsWochenTag, requestWochenuebersicht } from "../Timesafe/timesafeApiCalls";

const WOCHENTAGE = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

function formatMinutes(min: number): string {
    const sign = min < 0 ? "-" : "";
    const abs = Math.abs(min);
    return `${sign}${Math.floor(abs / 60)}:${(abs % 60).toString().padStart(2, "0")}`;
}

// yyyy-MM-dd as a local date (no time zone shift)
function parseDay(day: string): Date {
    const [y, m, d] = day.split("-").map(Number);
    return new Date(y, m - 1, d);
}

function toDay(date: Date): string {
    const pad = (n: number) => n.toString().padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function addDays(day: string, days: number): string {
    const date = parseDay(day);
    date.setDate(date.getDate() + days);
    return toDay(date);
}

function isoWeek(day: string): number {
    const date = parseDay(day);
    date.setDate(date.getDate() + 3 - ((date.getDay() + 6) % 7)); // Thursday of this week
    const firstThursday = new Date(date.getFullYear(), 0, 4);
    return 1 + Math.round(((date.getTime() - firstThursday.getTime()) / 86400000 - 3 + ((firstThursday.getDay() + 6) % 7)) / 7);
}

function formatShortDate(day: string): string {
    return parseDay(day).toLocaleDateString("de-CH", { day: "2-digit", month: "2-digit" });
}

function Differenz(props: { ist: number; soll: number | null }) {
    if (props.soll === null) return <span className="week__diff">–</span>;
    const diff = props.ist - props.soll;
    const klasse = diff > 0 ? "plus" : diff < 0 ? "minus" : "";
    return <span className={`week__diff ${klasse}`}>{`${diff > 0 ? "+" : ""}${formatMinutes(diff)}`}</span>;
}

function TagSpalte(props: { tag: ITsWochenTag; index: number; heute: boolean }) {
    const { tag } = props;
    const anteil = tag.sollMinuten ? Math.min(tag.istMinuten / tag.sollMinuten, 1.5) : tag.istMinuten > 0 ? 1 : 0;
    return (
        <div className={`week__day ${props.heute ? "today" : ""}`} title={tag.feiertag ?? ""}>
            <div className="week__label">
                {WOCHENTAGE[props.index]} {formatShortDate(tag.datum)}
            </div>
            <div className="week__bar">
                <div
                    className={`week__bar-fill ${tag.sollMinuten && tag.istMinuten >= tag.sollMinuten ? "full" : ""}`}
                    style={{ height: `${(anteil / 1.5) * 100}%` }}
                />
                {tag.sollMinuten ? <div className="week__bar-soll" style={{ bottom: `${(1 / 1.5) * 100}%` }} /> : null}
            </div>
            <div className="week__ist">{formatMinutes(tag.istMinuten)}</div>
            <div className="week__soll">Soll {tag.sollMinuten === null ? "–" : formatMinutes(tag.sollMinuten)}</div>
            <Differenz ist={tag.istMinuten} soll={tag.sollMinuten} />
            {tag.feiertag && <div className="week__holiday">{tag.feiertag}</div>}
        </div>
    );
}

// Booked (Ist) and target (Soll) hours per day of a week, from TimeSafe via thanos26
function ContainerWeekOverview(props: { classes: string }) {
    const { isActive } = useContext(TimeBookingContext);
    const [datum, setDatum] = useState<string>(toDay(new Date()));
    const [woche, setWoche] = useState<ITsWoche | null>(null);
    const [laden, setLaden] = useState<boolean>(true);
    const [fehler, setFehler] = useState<string>("");

    // Load on start, when changing the week and when clocking in/out (a new booking changes Ist)
    useEffect(() => {
        setLaden(true);
        requestWochenuebersicht(datum)
            .then((daten) => {
                setWoche(daten);
                setFehler("");
            })
            .catch((error) => {
                console.error(error);
                setFehler(`Wochenübersicht konnte nicht geladen werden: ${error?.message ?? error}`);
            })
            .finally(() => setLaden(false));
    }, [datum, isActive]);

    const heute = toDay(new Date());

    let inhalt: React.ReactNode;
    if (fehler) inhalt = <div className="ds-list__empty ds-list__error">{fehler}</div>;
    else if (!woche) inhalt = <div className="ds-list__empty">Wochenübersicht wird geladen ...</div>;
    else
        inhalt = (
            <>
                <div className="week__days">
                    {woche.tage.map((tag, index) => (
                        <TagSpalte key={tag.datum} tag={tag} index={index} heute={tag.datum === heute} />
                    ))}
                    <div className="week__day week__total">
                        <div className="week__label">Woche</div>
                        <div className="week__ist">{formatMinutes(woche.summeIstMinuten)}</div>
                        <div className="week__soll">
                            Soll {woche.summeSollMinuten === null ? "–" : formatMinutes(woche.summeSollMinuten)}
                        </div>
                        <Differenz ist={woche.summeIstMinuten} soll={woche.summeSollMinuten} />
                    </div>
                </div>
                {woche.hinweis && <div className="week__hint">{woche.hinweis}</div>}
            </>
        );

    return (
        <DashboardContainer headerName="WOCHENÜBERSICHT" className={`ds-list ds-weekoverview ${props.classes}`}>
            <div className="week__nav">
                <button title="Vorherige Woche" onClick={() => setDatum(addDays(datum, -7))}>
                    ‹
                </button>
                <span>
                    {woche ? `KW ${isoWeek(woche.von)} · ${formatShortDate(woche.von)} – ${formatShortDate(woche.bis)}` : ""}
                    {laden && woche ? " …" : ""}
                </span>
                <button title="Nächste Woche" onClick={() => setDatum(addDays(datum, 7))}>
                    ›
                </button>
                <button className="week__today" title="Aktuelle Woche" onClick={() => setDatum(toDay(new Date()))}>
                    Heute
                </button>
            </div>
            {inhalt}
        </DashboardContainer>
    );
}

export default ContainerWeekOverview;
